import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { useTasks } from "@/hooks/use-tasks";
import { useToast } from "@/hooks/use-toast";
import { TaskPriority } from "@/api/client";

const createTaskSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().optional(),
    priority: z.enum(["low", "medium", "high", "urgent"]).default("medium"),
    dueDate: z.string().optional(),
    assignedTo: z.string().optional(),
});

type CreateTaskFormValues = z.infer<typeof createTaskSchema>;

interface CreateTaskModalProps {
    claimId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateTaskModal({ claimId, open, onOpenChange }: CreateTaskModalProps) {
    const { createTask, isCreating, assignableUsers } = useTasks(claimId);
    const { toast } = useToast();

    const form = useForm<CreateTaskFormValues>({
        resolver: zodResolver(createTaskSchema) as any,
        defaultValues: {
            title: "",
            description: "",
            priority: "medium",
            dueDate: "",
            assignedTo: "",
        }
    });

    const onSubmit = (data: CreateTaskFormValues) => {
        // Parse date string to create a Date at noon local time to avoid timezone issues
        let dueDate: Date | undefined;
        if (data.dueDate) {
            const [year, month, day] = data.dueDate.split('-').map(Number);
            dueDate = new Date(year, month - 1, day, 12, 0, 0);
        }

        createTask({
            claimId,
            title: data.title,
            description: data.description,
            priority: data.priority as TaskPriority,
            dueDate,
            assignedTo: data.assignedTo ? Number(data.assignedTo) : undefined,
        }, {
            onSuccess: () => {
                toast({ title: "Success", description: "Task created successfully" });
                form.reset();
                onOpenChange(false);
            },
            onError: () => {
                toast({ title: "Error", description: "Failed to create task", variant: "destructive" });
            }
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Create New Task</DialogTitle>
                    <DialogDescription>
                        Add a new task to this claim.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit) as any} className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                            id="title"
                            placeholder="Task title"
                            {...form.register("title")}
                        />
                        {form.formState.errors.title && (
                            <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            placeholder="Task details..."
                            {...form.register("description")}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="priority">Priority</Label>
                            <Select
                                onValueChange={(val) => form.setValue("priority", val as any)}
                                defaultValue={form.getValues("priority")}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select priority" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="dueDate">Due Date</Label>
                            <Input
                                id="dueDate"
                                type="date"
                                {...form.register("dueDate")}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="assignedTo">Assignee</Label>
                        <Select
                            onValueChange={(val) => form.setValue("assignedTo", val === "unassigned" ? "" : val)}
                            value={form.watch("assignedTo") || "unassigned"}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select assignee" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                {assignableUsers.map((u) => (
                                    <SelectItem key={u.id} value={String(u.id)}>
                                        {u.name || u.email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter className="pt-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isCreating} className="gap-2">
                            {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Create Task
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
